/**
 * Knowledge Base Service
 * Per-organization RAG document management, topics, and chat.
 */

import axiosInstance, { apiRequest } from '../axiosInstance';

const baseFor = (orgId) => `/api/organizations/${orgId}/knowledgebase`;

const knowledgeBaseService = {
  // ===== Documents =====

  listDocuments: (organizationId, { search, status } = {}) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/documents`, {
        params: { search, status },
      })
    ),

  getDocument: (organizationId, documentId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/documents/${documentId}`)
    ),

  /**
   * Step 1 of upload: register the document and get a presigned PUT URL.
   * Step 2 (caller): PUT the file to `uploadUrl` with the same Content-Type.
   */
  createDocument: (organizationId, payload) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/documents`, payload)
    ),

  updateDocument: (organizationId, documentId, payload) =>
    apiRequest(() =>
      axiosInstance.put(`${baseFor(organizationId)}/documents/${documentId}`, payload)
    ),

  uploadFileToPresigned: async (uploadUrl, file, contentType) => {
    // Direct PUT — bypasses axiosInstance to avoid the JSON Content-Type interceptor.
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`S3 upload failed (${res.status}): ${text || res.statusText}`);
    }
    return true;
  },

  deleteDocument: (organizationId, documentId) =>
    apiRequest(() =>
      axiosInstance.delete(`${baseFor(organizationId)}/documents/${documentId}`)
    ),

  analyzeDocument: (organizationId, payload) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/documents/analyze`, payload)
    ),

  assignTopics: (organizationId, documentId, topicIds) =>
    apiRequest(() =>
      axiosInstance.put(`${baseFor(organizationId)}/documents/${documentId}/topics`, { topicIds })
    ),

  getDocumentDownloadUrl: (organizationId, documentId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/documents/${documentId}/url`)
    ),

  getDocumentPageUrl: (organizationId, documentId, pageNumber) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/documents/${documentId}/page/${pageNumber}`)
    ),

  // ===== Sync =====

  triggerSync: (organizationId) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/sync`)
    ),

  getSyncStatus: (organizationId, ingestionJobId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/sync/${ingestionJobId}`)
    ),

  // ===== Topics =====

  getTopics: (organizationId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/topics`)
    ),

  createTopic: (organizationId, payload) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/topics`, payload)
    ),

  updateTopic: (organizationId, topicId, payload) =>
    apiRequest(() =>
      axiosInstance.put(`${baseFor(organizationId)}/topics/${topicId}`, payload)
    ),

  deleteTopic: (organizationId, topicId) =>
    apiRequest(() =>
      axiosInstance.delete(`${baseFor(organizationId)}/topics/${topicId}`)
    ),

  /**
   * Returns AI-generated topic suggestions WITHOUT persisting them.
   * Caller should review/edit, then commit via createTopicsBatch.
   * @param {{prompt?: string, count?: number}} payload
   */
  generateTopicSuggestions: (organizationId, payload = {}) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/topics/generate`, payload)
    ),

  /**
   * Persist a list of topics in one batch. Used to commit the AI suggestions
   * the user reviewed in the GenerateTopicsModal.
   * @param {{topics: Array, markAiGenerated?: boolean}} payload
   */
  createTopicsBatch: (organizationId, payload) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/topics/batch`, payload)
    ),

  autoAssignDocuments: (organizationId) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/topics/auto-assign`)
    ),

  // ===== Chat =====

  chat: (organizationId, query, sessionId = null) =>
    apiRequest(() =>
      axiosInstance.post(`${baseFor(organizationId)}/chat`, { query, sessionId })
    ),

  listSessions: (organizationId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/sessions`)
    ),

  getSession: (organizationId, sessionId) =>
    apiRequest(() =>
      axiosInstance.get(`${baseFor(organizationId)}/sessions/${sessionId}`)
    ),

  deleteSession: (organizationId, sessionId) =>
    apiRequest(() =>
      axiosInstance.delete(`${baseFor(organizationId)}/sessions/${sessionId}`)
    ),
};

export const KNOWLEDGE_DOCUMENT_STATUS_LABELS = {
  Uploaded: 'Pending sync',
  Processing: 'Indexing…',
  Indexed: 'Ready',
  Failed: 'Failed',
  PendingDeletion: 'Deleting…',
};

// Curated icons offered first in the topic icon picker. MUST stay in sync
// with the AllowedIcons array in BedrockKnowledgeBaseService.cs so AI-suggested
// icons always render in both web and mobile.
export const KB_TOPIC_ICONS = [
  'file-text', 'book', 'book-open', 'folder', 'folder-open', 'clipboard-list',
  'shield', 'shield-check', 'wrench', 'settings', 'users', 'building',
  'building-2', 'bar-chart', 'bar-chart-3', 'pie-chart', 'calendar', 'clock',
  'map-pin', 'package', 'truck', 'dollar-sign', 'credit-card', 'briefcase',
  'award', 'flag', 'bell', 'lightbulb', 'target', 'compass', 'globe',
  'lock', 'key', 'database', 'server', 'cloud', 'mail', 'phone', 'message-square',
];

export default knowledgeBaseService;
